import { FIELD_SIZES, LANE_COUNT, QUALIFIERS_PER_HEAT, type FieldSize } from './constants';
import { shuffle, type Rng } from './rng';
import type { Entrant, Heat, HeatResult, Round } from './types';

export function isValidFieldSize(value: number): value is FieldSize {
  return (FIELD_SIZES as readonly number[]).includes(value);
}

export function getRoundCount(fieldSize: FieldSize): number {
  return Math.log2(fieldSize / LANE_COUNT) + 1;
}

export function getRoundName(entrantsInRound: number): string {
  switch (entrantsInRound) {
    case LANE_COUNT:
      return 'Finale';
    case 16:
      return 'Demi-finales';
    case 32:
      return 'Quarts de finale';
    case 64:
      return 'Huitièmes de finale';
    case 128:
      return 'Seizièmes de finale';
    default:
      return `Tour de ${entrantsInRound}`;
  }
}

export function getHeatId(roundIndex: number, heatIndex: number): string {
  return `r${roundIndex}-h${heatIndex}`;
}

/**
 * Construit l'arbre complet du tournoi. Le premier tour est tiré au sort ; les tours suivants
 * restent vides jusqu'à ce que {@link populateNextRound} les remplisse.
 * @param entrants tous les participants (humains + bots), en nombre égal à une taille de tournoi valide.
 */
export function createBracket(entrants: readonly Entrant[], rng: Rng = Math.random): Round[] {
  const fieldSize = entrants.length;
  if (!isValidFieldSize(fieldSize)) {
    throw new RangeError(`Taille de tournoi invalide : ${fieldSize}`);
  }

  const rounds: Round[] = [];
  const roundCount = getRoundCount(fieldSize);
  for (let r = 0; r < roundCount; r++) {
    const entrantsInRound = fieldSize / 2 ** r;
    const heatCount = entrantsInRound / LANE_COUNT;
    const heats: Heat[] = [];
    for (let h = 0; h < heatCount; h++) {
      heats.push({
        id: getHeatId(r, h),
        roundIndex: r,
        index: h,
        lanes: [],
        status: 'pending',
        results: null,
      });
    }
    rounds.push({ index: r, name: getRoundName(entrantsInRound), heats });
  }

  const seeded = shuffle(entrants, rng);
  (rounds[0] as Round).heats.forEach((heat, h) => {
    heat.lanes = seeded.slice(h * LANE_COUNT, (h + 1) * LANE_COUNT);
  });
  return rounds;
}

/**
 * Classe les coureurs d'une série : les arrivés par temps croissant, puis les non-arrivés
 * par distance décroissante. En finale, seul le vainqueur est marqué « qualifié ».
 */
export function rankHeat(
  entries: readonly Omit<HeatResult, 'rank' | 'qualified'>[],
  isFinal: boolean,
): HeatResult[] {
  const sorted = [...entries].sort((a, b) => {
    if (a.timeMs !== null && b.timeMs !== null) return a.timeMs - b.timeMs;
    if (a.timeMs !== null) return -1;
    if (b.timeMs !== null) return 1;
    return b.distanceM - a.distanceM;
  });
  const qualifierCount = isFinal ? 1 : QUALIFIERS_PER_HEAT;
  return sorted.map((entry, i) => ({ ...entry, rank: i + 1, qualified: i < qualifierCount }));
}

export function findHeat(rounds: readonly Round[], heatId: string): Heat | undefined {
  for (const round of rounds) {
    const heat = round.heats.find((candidate) => candidate.id === heatId);
    if (heat) return heat;
  }
  return undefined;
}

export function isRoundComplete(round: Round): boolean {
  return round.heats.every((heat) => heat.status === 'done');
}

/**
 * Remplit le tour suivant : chaque série accueille les qualifiés de deux séries voisines
 * (ce qui donne un arbre lisible), placés dans des couloirs tirés au sort.
 */
export function populateNextRound(
  rounds: readonly Round[],
  roundIndex: number,
  rng: Rng = Math.random,
): void {
  const current = rounds[roundIndex];
  const next = rounds[roundIndex + 1];
  if (!current || !next) throw new RangeError(`Pas de tour après le tour ${roundIndex}`);

  const qualifiersOf = (heat: Heat | undefined): Entrant[] => {
    if (!heat?.results) return [];
    const byId = new Map(heat.lanes.map((entrant) => [entrant.id, entrant]));
    return heat.results
      .filter((result) => result.qualified)
      .map((result) => byId.get(result.entrantId))
      .filter((entrant): entrant is Entrant => entrant !== undefined);
  };

  next.heats.forEach((heat, k) => {
    const feeders = [current.heats[2 * k], current.heats[2 * k + 1]];
    heat.lanes = shuffle(feeders.flatMap(qualifiersOf), rng);
    heat.status = 'pending';
  });
}

/** Vainqueur de la finale, ou `null` si elle n'est pas terminée. */
export function getChampion(rounds: readonly Round[]): Entrant | null {
  const finalHeat = rounds[rounds.length - 1]?.heats[0];
  const winner = finalHeat?.results?.find((result) => result.rank === 1);
  return finalHeat?.lanes.find((entrant) => entrant.id === winner?.entrantId) ?? null;
}
